import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Login.css';
import api from '../services/api';

const ResetWachtwoord = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [nieuwWachtwoord, setNieuwWachtwoord] = useState('');
  const [bevestigWachtwoord, setBevestigWachtwoord] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [gebruiker, setGebruiker] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Geen reset token gevonden');
      setVerifying(false);
      return;
    }

    setToken(tokenParam);
    verifyToken(tokenParam);
  }, [searchParams]);

  const verifyToken = async (tokenValue) => {
    try {
      const response = await api.post('/password-reset/verify', { token: tokenValue });
      
      if (response.data.success) {
        setValidToken(true);
        setGebruiker(response.data.gebruiker);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Deze reset link is ongeldig of verlopen');
      setValidToken(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validatie
    const wwRegels = [
      nieuwWachtwoord.length >= 10,
      /[A-Z]/.test(nieuwWachtwoord),
      /[0-9]/.test(nieuwWachtwoord),
      /[^A-Za-z0-9]/.test(nieuwWachtwoord),
    ];
    if (!wwRegels.every(Boolean)) {
      setError('Wachtwoord voldoet niet aan de eisen: min. 10 tekens, hoofdletter, cijfer en speciaal teken');
      return;
    }

    if (nieuwWachtwoord !== bevestigWachtwoord) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/password-reset/reset', {
        token,
        nieuwWachtwoord
      });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Er is iets misgegaan');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Reset link verifiëren...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>❌ Ongeldige Link</h1>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>{error || 'Deze reset link is ongeldig of verlopen.'}</p>
            <button 
              onClick={() => navigate('/wachtwoord-vergeten')}
              className="btn-primary"
              style={{ marginTop: '20px' }}
            >
              Nieuwe Link Aanvragen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>✅ Wachtwoord Gewijzigd</h1>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Je wachtwoord is succesvol gewijzigd!</p>
            <p style={{ marginTop: '10px', color: '#666' }}>
              Je kunt nu inloggen met je nieuwe wachtwoord.
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary"
              style={{ marginTop: '30px' }}
            >
              Naar Inloggen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Nieuw Wachtwoord</h1>
          <p>Welkom {gebruiker?.voornaam}! Kies een nieuw wachtwoord.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="nieuw-wachtwoord" style={{color:"#00D4AA"}}>Nieuw Wachtwoord</label>
            <input
              type="password" minLength="10" placeholder="Min. 10 tekens + hoofdletter + cijfer + speciaal"
              id="nieuw-wachtwoord"
              value={nieuwWachtwoord}
              onChange={(e) => setNieuwWachtwoord(e.target.value)}
              required
              autoFocus
              disabled={loading}
              style={{background:"rgba(255,255,255,0.05)",color:"#fff",border:"1.5px solid rgba(0,212,170,0.3)",borderRadius:"10px"}}
            />
            <ul style={{listStyle:"none",padding:0,margin:"10px 0 0",fontSize:"13px"}}>
              {[
                ["Minimaal 10 tekens", nieuwWachtwoord.length >= 10],
                ["Minstens \u00e9\u00e9n hoofdletter", /[A-Z]/.test(nieuwWachtwoord)],
                ["Minstens \u00e9\u00e9n cijfer", /[0-9]/.test(nieuwWachtwoord)],
                ["Minstens \u00e9\u00e9n speciaal teken", /[^A-Za-z0-9]/.test(nieuwWachtwoord)],
              ].map(([label, ok]) => (
                <li key={label} style={{color: ok ? "#00D4AA" : "#94a3b8", margin:"3px 0", display:"flex", alignItems:"center", gap:"6px"}}>
                  <span>{ok ? "\u2713" : "\u25cb"}</span> {label}
                </li>
              ))}
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="bevestig-wachtwoord" style={{color:"#00D4AA"}}>Bevestig Wachtwoord</label>
            <input
              type="password" minLength="10" placeholder="Min. 10 tekens + hoofdletter + cijfer + speciaal"
              id="bevestig-wachtwoord"
              value={bevestigWachtwoord}
              onChange={(e) => setBevestigWachtwoord(e.target.value)}
              required
              disabled={loading}
              placeholder="Herhaal wachtwoord"
              style={{background:"rgba(255,255,255,0.05)",color:"#fff",border:"1.5px solid rgba(0,212,170,0.3)",borderRadius:"10px"}}
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Opslaan...' : 'Wachtwoord Wijzigen'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetWachtwoord;
